import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  pcGetConfig, pcConnect, pcTest, pcDisconnect,
  pcListPatients, pcCreatePatient, pcListAppointments, pcBookAppointment,
  pcListEncounters, pcCreateEncounter,
} from "@/lib/api";
import {
  Plug, CheckCircle2, KeyRound, RefreshCw, Trash2, Search, UserPlus, CalendarPlus,
  FileText, ChevronDown, Loader2, ShieldCheck, BookOpen, Server, Zap,
} from "lucide-react";

const BASE_DEFAULT = "https://proactiveclinics.com/api/v1";
const SCOPE_LABELS = {
  "patients:read": "List & fetch patients",
  "patients:write": "Register patients",
  "appointments:read": "List appointments & availability",
  "appointments:write": "Book appointments",
  "encounters:read": "List & fetch visit notes",
  "encounters:write": "Create visit notes",
};

function Field({ label, value, onChange, placeholder, type = "text", mono }) {
  return (
    <div>
      <label className="text-[12.5px] font-semibold text-[#33425c]">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-[#1a5fd0] ${mono ? "font-mono" : ""}`} />
    </div>
  );
}

function ResultPanel({ res }) {
  if (!res) return null;
  const ok = res.status < 300;
  return (
    <div className="mt-4 rounded-2xl border border-slate-100 overflow-hidden">
      <div className={`flex items-center gap-2 px-4 py-2 text-[12.5px] font-bold ${ok ? "bg-[#e6f7ec] text-[#2f9e57]" : "bg-[#fdecec] text-[#e23b3b]"}`}>
        <span className={`h-2 w-2 rounded-full ${ok ? "bg-[#2f9e57]" : "bg-[#e23b3b]"}`} /> HTTP {res.status} {ok ? "OK" : "Error"}
      </div>
      <pre className="max-h-72 overflow-auto thin-scroll bg-[#0f2a5e] text-[#dbe7ff] text-[12px] leading-relaxed p-4 m-0">{JSON.stringify(res.body, null, 2)}</pre>
    </div>
  );
}

const Btn = ({ onClick, busy, children, icon: Ic, variant = "primary" }) => (
  <button onClick={onClick} disabled={busy}
    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[14px] font-semibold disabled:opacity-60 ${variant === "primary" ? "btn-primary" : "btn-ghost-blue"}`}>
    {busy ? <Loader2 size={16} className="animate-spin" /> : Ic && <Ic size={16} />} {children}
  </button>
);

export default function ProactiveClinicsIntegration() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ key_id: "", secret: "", base_url: BASE_DEFAULT });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("patients");
  const [showRef, setShowRef] = useState(false);

  const load = () => pcGetConfig().then(setCfg).catch(() => {});
  useEffect(() => { load(); }, []);

  const connected = cfg?.connected;

  const connect = async () => {
    if (!form.key_id || !form.secret) { toast.error("Enter both Key ID and Secret"); return; }
    setBusy(true);
    try {
      const r = await pcConnect(form);
      if (r.data.connected) {
        toast.success(`Connected to ProactiveClinics as “${r.data.result.app}”`);
        setForm({ ...form, secret: "" });
      } else {
        toast.error(r.data?.result?.error?.message || "Connection failed — check your credentials");
      }
      await load();
    } catch { toast.error("Connection failed"); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const r = await pcTest();
      r.data.connected ? toast.success(`Verified — ${r.data.result.app}`) : toast.error(r.data?.result?.error?.message || "Verification failed");
      await load();
    } catch { toast.error("Test failed"); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await pcDisconnect(); toast.success("Disconnected"); setForm({ key_id: "", secret: "", base_url: BASE_DEFAULT }); await load(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="card-soft p-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <span className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#2f7bf0] to-[#0f2a5e] grid place-items-center text-white shadow-md shrink-0"><Server size={26} /></span>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-[19px] font-bold text-[#10233f]">ProactiveClinics EHR</h3>
            {connected ? (
              <span className="chip bg-[#e6f7ec] text-[#2f9e57] inline-flex items-center gap-1"><CheckCircle2 size={13} /> Connected</span>
            ) : (
              <span className="chip bg-[#f1f4f9] text-[#7488a8]">Not connected</span>
            )}
          </div>
          <p className="text-[13.5px] text-slate-500 mt-0.5">Exchange patients, appointments, and visit notes with ProactiveClinics via secure REST API. Your secret is stored server-side only.</p>
          {connected && cfg?.app && <p className="text-[12.5px] text-slate-500 mt-1">App: <b className="text-[#10233f]">{cfg.app}</b> · Key <span className="font-mono">{cfg.key_id}</span></p>}
        </div>
      </div>

      {/* Credentials */}
      <div className="mt-5 grid md:grid-cols-2 gap-3">
        <Field label="Key ID (pck_…)" value={connected ? cfg.key_id : form.key_id} onChange={(v) => setForm({ ...form, key_id: v })} placeholder="pck_xxxxxxxx" mono />
        <Field label="Secret (pcs_…)" type="password" value={connected ? "" : form.secret} onChange={(v) => setForm({ ...form, secret: v })} placeholder={connected ? cfg.secret_masked || "•••• stored" : "pcs_xxxxxxxxxxxx"} mono />
        <div className="md:col-span-2">
          <Field label="Base URL" value={connected ? cfg.base_url : form.base_url} onChange={(v) => setForm({ ...form, base_url: v })} placeholder={BASE_DEFAULT} mono />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2.5">
        {!connected && <Btn onClick={connect} busy={busy} icon={Plug} data-testid="pc-connect">Save &amp; Connect</Btn>}
        {connected && <Btn onClick={test} busy={busy} icon={RefreshCw} variant="ghost">Test Connection</Btn>}
        {connected && <Btn onClick={connect} busy={busy} icon={KeyRound} variant="ghost">Update Key</Btn>}
        {connected && <button onClick={disconnect} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-[#e23b3b] bg-[#fdecec] hover:bg-[#fbe0e0] disabled:opacity-60"><Trash2 size={16} /> Disconnect</button>}
      </div>

      {/* Scopes */}
      {connected && cfg?.scopes?.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center gap-1.5 text-[12.5px] font-bold text-[#10233f] mb-2"><ShieldCheck size={15} className="text-[#1a5fd0]" /> Granted scopes</div>
          <div className="flex flex-wrap gap-2">
            {cfg.scopes.map((s) => (
              <span key={s} className="chip bg-[#eef5ff] text-[#1a5fd0] inline-flex items-center gap-1.5"><span className="font-mono">{s}</span><span className="text-slate-400 font-normal">· {SCOPE_LABELS[s] || ""}</span></span>
            ))}
          </div>
        </div>
      )}

      {/* Explorer */}
      {connected && (
        <div className="mt-6 border-t border-slate-100 pt-5">
          <div className="flex items-center gap-1.5 mb-3"><Zap size={16} className="text-[#1a5fd0]" /><h4 className="font-display text-[16px] font-bold text-[#10233f]">API Explorer</h4></div>
          <div className="flex gap-1 border-b border-slate-100 mb-4">
            {[["patients", "Patients", Search], ["appointments", "Appointments", CalendarPlus], ["encounters", "Encounters", FileText]].map(([id, label, Ic]) => (
              <button key={id} onClick={() => setTab(id)} className={`flex items-center gap-1.5 px-3 py-2 text-[13.5px] font-semibold border-b-2 -mb-px ${tab === id ? "border-[#1a5fd0] text-[#1a5fd0]" : "border-transparent text-slate-400 hover:text-slate-600"}`}><Ic size={15} /> {label}</button>
            ))}
          </div>
          {tab === "patients" && <PatientsTab scopes={cfg.scopes} />}
          {tab === "appointments" && <AppointmentsTab scopes={cfg.scopes} />}
          {tab === "encounters" && <EncountersTab scopes={cfg.scopes} />}
        </div>
      )}

      {/* Reference */}
      <div className="mt-6 border-t border-slate-100 pt-4">
        <button onClick={() => setShowRef(!showRef)} className="flex items-center gap-2 text-[14px] font-bold text-[#1a5fd0]"><BookOpen size={16} /> API Reference <ChevronDown size={16} className={`transition-transform ${showRef ? "rotate-180" : ""}`} /></button>
        {showRef && <Reference />}
      </div>
    </div>
  );
}

function useRun() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { const r = await fn(); setRes({ status: r.status, body: r.data }); }
    catch (e) { setRes({ status: e?.response?.status || 0, body: e?.response?.data || { error: { message: "Request failed" } } }); }
    finally { setBusy(false); }
  };
  return { res, busy, run };
}

function Gate({ scopes, need, children }) {
  if (!scopes.includes(need)) return <div className="text-[13px] text-slate-400 bg-slate-50 rounded-xl px-3 py-2.5">Requires scope <span className="font-mono text-[#e0842f]">{need}</span> — not granted to this key.</div>;
  return children;
}

function PatientsTab({ scopes }) {
  const [q, setQ] = useState("");
  const [np, setNp] = useState({ firstName: "", lastName: "", dob: "", sex: "F", email: "" });
  const list = useRun();
  const create = useRun();
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <div className="text-[13px] font-bold text-[#10233f] mb-2">Search patients <span className="font-mono text-slate-400 font-normal">GET /patients</span></div>
        <Gate scopes={scopes} need="patients:read">
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name / MRN / email" className="flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#1a5fd0]" />
            <Btn onClick={() => list.run(() => pcListPatients({ query: q, limit: 10 }))} busy={list.busy} icon={Search}>Search</Btn>
          </div>
          {list.res?.body?.data && (
            <div className="mt-3 rounded-xl border border-slate-100 overflow-hidden">
              <table className="w-full text-[12.5px]"><thead className="bg-slate-50 text-slate-500"><tr><th className="text-left px-3 py-2">MRN</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">DOB</th></tr></thead>
                <tbody>{list.res.body.data.map((p) => (<tr key={p.id} className="border-t border-slate-50"><td className="px-3 py-2 font-mono">{p.mrn}</td><td className="px-3 py-2">{p.firstName} {p.lastName}</td><td className="px-3 py-2">{p.dob}</td></tr>))}
                  {list.res.body.data.length === 0 && <tr><td colSpan={3} className="px-3 py-3 text-slate-400">No matches</td></tr>}</tbody></table>
            </div>
          )}
          <ResultPanel res={list.res} />
        </Gate>
      </div>
      <div>
        <div className="text-[13px] font-bold text-[#10233f] mb-2">Register patient <span className="font-mono text-slate-400 font-normal">POST /patients</span></div>
        <Gate scopes={scopes} need="patients:write">
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name" value={np.firstName} onChange={(v) => setNp({ ...np, firstName: v })} placeholder="Jamie" />
            <Field label="Last name" value={np.lastName} onChange={(v) => setNp({ ...np, lastName: v })} placeholder="Rivera" />
            <Field label="DOB" type="date" value={np.dob} onChange={(v) => setNp({ ...np, dob: v })} />
            <div><label className="text-[12.5px] font-semibold text-[#33425c]">Sex</label><select value={np.sex} onChange={(e) => setNp({ ...np, sex: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1a5fd0]">{["F", "M", "X"].map((o) => <option key={o}>{o}</option>)}</select></div>
            <div className="col-span-2"><Field label="Email (optional)" value={np.email} onChange={(v) => setNp({ ...np, email: v })} placeholder="jamie@example.com" /></div>
          </div>
          <div className="mt-3"><Btn onClick={() => create.run(() => pcCreatePatient(np))} busy={create.busy} icon={UserPlus}>Register</Btn></div>
          <ResultPanel res={create.res} />
        </Gate>
      </div>
    </div>
  );
}

function AppointmentsTab({ scopes }) {
  const [f, setF] = useState({ patientId: "", from: "", to: "" });
  const [b, setB] = useState({ patientId: "", providerId: "", day: "", time: "", reason: "" });
  const list = useRun();
  const book = useRun();
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <div className="text-[13px] font-bold text-[#10233f] mb-2">List appointments <span className="font-mono text-slate-400 font-normal">GET /appointments</span></div>
        <Gate scopes={scopes} need="appointments:read">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Patient ID" value={f.patientId} onChange={(v) => setF({ ...f, patientId: v })} placeholder="opt." mono />
            <Field label="From" type="date" value={f.from} onChange={(v) => setF({ ...f, from: v })} />
            <Field label="To" type="date" value={f.to} onChange={(v) => setF({ ...f, to: v })} />
          </div>
          <div className="mt-3"><Btn onClick={() => list.run(() => pcListAppointments({ patientId: f.patientId, date_from: f.from, date_to: f.to, limit: 50 }))} busy={list.busy} icon={Search}>List</Btn></div>
          <ResultPanel res={list.res} />
        </Gate>
      </div>
      <div>
        <div className="text-[13px] font-bold text-[#10233f] mb-2">Book appointment <span className="font-mono text-slate-400 font-normal">POST /appointments</span></div>
        <Gate scopes={scopes} need="appointments:write">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Patient ID" value={b.patientId} onChange={(v) => setB({ ...b, patientId: v })} placeholder="required" mono />
            <Field label="Provider ID" value={b.providerId} onChange={(v) => setB({ ...b, providerId: v })} placeholder="required" mono />
            <Field label="Day" type="date" value={b.day} onChange={(v) => setB({ ...b, day: v })} />
            <Field label="Time" type="time" value={b.time} onChange={(v) => setB({ ...b, time: v })} />
            <div className="col-span-2"><Field label="Reason" value={b.reason} onChange={(v) => setB({ ...b, reason: v })} placeholder="Therapy intake" /></div>
          </div>
          <div className="mt-3"><Btn onClick={() => book.run(() => pcBookAppointment(b))} busy={book.busy} icon={CalendarPlus}>Book</Btn></div>
          <ResultPanel res={book.res} />
        </Gate>
      </div>
    </div>
  );
}

function EncountersTab({ scopes }) {
  const [pid, setPid] = useState("");
  const [e, setE] = useState({ patientId: "", providerId: "", visitType: "", chiefComplaint: "", subjective: "", objective: "", assessment: "", plan: "" });
  const list = useRun();
  const create = useRun();
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <div className="text-[13px] font-bold text-[#10233f] mb-2">List encounters <span className="font-mono text-slate-400 font-normal">GET /encounters</span></div>
        <Gate scopes={scopes} need="encounters:read">
          <div className="flex gap-2"><input value={pid} onChange={(ev) => setPid(ev.target.value)} placeholder="patientId (optional)" className="flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] font-mono outline-none focus:border-[#1a5fd0]" /><Btn onClick={() => list.run(() => pcListEncounters({ patientId: pid, limit: 20 }))} busy={list.busy} icon={Search}>List</Btn></div>
          <ResultPanel res={list.res} />
        </Gate>
      </div>
      <div>
        <div className="text-[13px] font-bold text-[#10233f] mb-2">Create visit note <span className="font-mono text-slate-400 font-normal">POST /encounters</span></div>
        <Gate scopes={scopes} need="encounters:write">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Patient ID" value={e.patientId} onChange={(v) => setE({ ...e, patientId: v })} placeholder="required" mono />
            <Field label="Provider ID" value={e.providerId} onChange={(v) => setE({ ...e, providerId: v })} placeholder="required" mono />
            <div className="col-span-2"><Field label="Chief complaint" value={e.chiefComplaint} onChange={(v) => setE({ ...e, chiefComplaint: v })} placeholder="e.g. Anxiety follow-up" /></div>
            <div className="col-span-2"><label className="text-[12.5px] font-semibold text-[#33425c]">Assessment / Plan</label><textarea value={e.plan} onChange={(v) => setE({ ...e, plan: v.target.value })} rows={2} className="mt-1 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#1a5fd0] resize-none" placeholder="Plan / notes" /></div>
          </div>
          <div className="mt-3"><Btn onClick={() => create.run(() => pcCreateEncounter(e))} busy={create.busy} icon={FileText}>Create note</Btn></div>
          <ResultPanel res={create.res} />
        </Gate>
      </div>
    </div>
  );
}

function Reference() {
  const Code = ({ children }) => <pre className="bg-[#0f2a5e] text-[#dbe7ff] text-[12px] rounded-xl p-3.5 overflow-auto thin-scroll my-2">{children}</pre>;
  const Ep = ({ m, p, s, children }) => (
    <div className="py-2 border-b border-slate-50">
      <div className="flex items-center gap-2 flex-wrap"><span className={`chip text-white ${m === "GET" ? "bg-[#2f9e57]" : "bg-[#1a5fd0]"}`}>{m}</span><span className="font-mono text-[13px] text-[#10233f]">{p}</span>{s && <span className="chip bg-[#f0eaff] text-[#7c53d6] font-mono">{s}</span>}</div>
      <p className="text-[13px] text-slate-500 mt-1">{children}</p>
    </div>
  );
  return (
    <div className="mt-4 text-[14px] text-[#33425c] space-y-2">
      <h4 className="font-display text-[16px] font-bold text-[#10233f]">Overview</h4>
      <p className="text-[13.5px] text-slate-500">JSON REST API secured with per-app API keys. Create and manage keys in Admin → API &amp; Integrations. Each key is granted only the scopes you select.</p>
      <p className="text-[13.5px]"><b>Base URL:</b> <span className="font-mono">https://proactiveclinics.com/api/v1</span></p>
      <h4 className="font-display text-[15px] font-bold text-[#10233f] mt-3">Authentication</h4>
      <p className="text-[13.5px] text-slate-500">Bearer token is the Key ID and secret joined with a dot: <span className="font-mono">{"{keyId}.{secret}"}</span>.</p>
      <Code>{`Authorization: Bearer {keyId}.{secret}
# or
X-API-Key: {keyId}.{secret}`}</Code>
      <Code>{`curl -s https://proactiveclinics.com/api/v1/ping \\
  -H "Authorization: Bearer $PC_API_TOKEN"
# 200 { "ok": true, "app": "…", "scopes": ["patients:read"] }`}</Code>
      <h4 className="font-display text-[15px] font-bold text-[#10233f] mt-3">Scopes</h4>
      <ul className="text-[13px] text-slate-500 space-y-1">
        {Object.entries(SCOPE_LABELS).map(([k, v]) => (<li key={k}><span className="font-mono text-[#1a5fd0]">{k}</span> — {v}</li>))}
      </ul>
      <h4 className="font-display text-[15px] font-bold text-[#10233f] mt-3">Endpoints</h4>
      <Ep m="GET" p="/ping">Returns the authenticated app, key ID and granted scopes. No scope required.</Ep>
      <Ep m="GET" p="/patients" s="patients:read">List patients. Query: query, status, limit (max 100), offset.</Ep>
      <Ep m="POST" p="/patients" s="patients:write">Register a patient. Required: firstName, lastName, dob (YYYY-MM-DD), sex (M|F|X).</Ep>
      <Ep m="GET" p="/patients/{id}" s="patients:read">Fetch a single patient by id.</Ep>
      <Ep m="GET" p="/appointments" s="appointments:read">List appointments. Query: patientId, providerId, from, to, limit (max 200).</Ep>
      <Ep m="POST" p="/appointments" s="appointments:write">Book. Required: patientId, providerId, day, time. autoShift snaps to next open same-day slot (response includes shifted).</Ep>
      <Ep m="GET" p="/encounters" s="encounters:read">List visit notes. Query: patientId, limit (max 100), offset.</Ep>
      <Ep m="POST" p="/encounters" s="encounters:write">Create a note. Required: patientId, providerId. Optional SOAP: visitType, chiefComplaint, subjective, objective, assessment, plan.</Ep>
      <h4 className="font-display text-[15px] font-bold text-[#10233f] mt-3">Errors</h4>
      <p className="text-[13px] text-slate-500">400 invalid_body · 401 unauthorized · 403 forbidden · 404 not_found · 409 conflict · 422 unschedulable · 429 rate_limited · 500/503 server error. All <span className="font-mono">/api/v1</span> endpoints send permissive CORS and answer OPTIONS preflight.</p>
    </div>
  );
}
